import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import PackageCard from '../components/PackageCard';
import CategoryPreview from '../components/CategoryPreview';
import HomeAbout from '../components/HomeAbout';
import HomeReviews from '../components/HomeReviews';
import SEO from '../components/SEO';
import { useProducts } from '../hooks/useProducts';
import { fadeInUpInView } from '../lib/motionPresets';
import { LoadingSpinner, LoadError } from '../components/AsyncState';

const TENT_BADGE_LABELS = ['PREMIUM', 'POPULAR', 'BEST VALUE'];
const TABLE_BADGE_LABELS = ['STARTER', 'UPGRADE', 'COMPLETE'];
const TENT_PACKAGE_IDS = ['package-9', 'package-1', 'package-8'];
const TABLE_PACKAGE_IDS = ['package-4', 'package-5', 'package-6'];

const Home = () => {
  const { products, loading, error, retry } = useProducts();

  const tentPackages = useMemo(
    () => products.filter((p) => p.category === 'Packages' && TENT_PACKAGE_IDS.includes(p.id)),
    [products]
  );

  const tablePackages = useMemo(
    () => products.filter((p) => p.category === 'Packages' && TABLE_PACKAGE_IDS.includes(p.id)),
    [products]
  );

  return (
    <>
      <SEO 
        title="Party Rentals Philadelphia & Delaware County | Thomas Party & Decor"
        description="Rent tents, tables, chairs, linens, and event décor from Thomas Party & Decor in Aldan, PA. Delivery to Philadelphia, Delaware County, the Main Line, and nearby areas. Request a quote."
        keywords="party rentals near me, party rentals Philadelphia, party rental Delaware County, chair rental near me, table rental near me, tent rental near me, throne chair rental near me, wedding rentals near me, event rentals Main Line, party supplies Delaware County, Montgomery County party rental, linen rental near me, tablecloth rental near me, spandex table cover rental"
        canonical="/"
      />
      <div className="min-h-screen" style={{ backgroundColor: '#FFF7ED' }}>
        <HeroSection />

        <section className="py-16 px-6 bg-white" data-testid="tent-packages-section">
          <div className="max-w-7xl mx-auto">
            <motion.div {...fadeInUpInView} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold heading-font mb-4" style={{ color: '#7C3AED' }}>
                Party Packages - Save Big!
              </h2>
              <p className="text-base sm:text-xl text-gray-700 max-w-3xl mx-auto">
                Everything you need in one convenient package. See exactly what&apos;s included!
              </p>
              <p className="text-lg text-orange-600 font-semibold mt-2">* Delivery priced separately based on location</p>
            </motion.div>

            <div className="mb-12">
              <h3 className="text-2xl font-bold heading-font mb-6 text-center" style={{ color: '#7C3AED' }}>Tent Packages - Setup, Breakdown & Lights Included</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {tentPackages.map((pkg, index) => (
                  <PackageCard key={pkg.id} pkg={pkg} index={index} badgeLabel={TENT_BADGE_LABELS[index] || 'PACKAGE'} bestValueIndex={0} />
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold heading-font mb-6 text-center" style={{ color: '#7C3AED' }}>Table & Chair Packages</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {tablePackages.map((pkg, index) => (
                  <PackageCard key={pkg.id} pkg={pkg} index={index} badgeLabel={TABLE_BADGE_LABELS[index] || 'PACKAGE'} bestValueIndex={2} />
                ))}
              </div>
            </div>

            <div className="text-center mt-10">
              <Link to="/packages" className="inline-block bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white px-10 py-4 rounded-full font-bold text-xl shadow-lg transition-all hover:scale-105">
                View All Packages
              </Link>
              <p className="text-gray-600 text-lg mt-4">
                Call <a href="tel:484-957-0076" className="text-orange-600 font-bold hover:underline">484-957-0076</a> to customize your package or get a delivery quote
              </p>
            </div>
          </div>
        </section>

        <HomeReviews />

        <HomeAbout />

        <section id="products" className="py-16 px-6" data-testid="products-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold heading-font text-center mb-4" style={{ color: '#7C3AED' }}>
              Our Party Rental Collection
            </h2>
            <p className="text-center text-gray-600 mb-16 text-lg">
              Browse our extensive selection organized by category
            </p>
            {loading && <LoadingSpinner />}
            {!loading && error && <LoadError message={error} onRetry={retry} />}
            {!loading && !error && (
              <div className="space-y-20">
                <CategoryPreview title="Chairs" category="Chairs" products={products} linkTo="/chairs" />
                <CategoryPreview title="Tables" category="Tables" products={products} linkTo="/tables" />
                <CategoryPreview title="Tents" category="Tents" products={products} linkTo="/tents" />
                <CategoryPreview title="Accessories & Linens" category="Accessories" products={products} linkTo="/accessories" />
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
